import React, { useState } from "react";

const faqs = [
  {
    category: "For Homeowners",
    question: "How does BuildBright work for homeowners?",
    answer:
      "BuildBright helps homeowners submit home improvement project requests, organize project intake, and coordinate next steps through available local contractors. Share your project details and we’ll help route the request through our review and coordination process. No cold calls, no spam, no hassle.",
  },
  {
    category: "For Homeowners",
    question: "Is it free for homeowners to use?",
    answer:
      "Yes. BuildBright is free for homeowners to submit a project request. There are no sign-up fees or subscription costs for homeowners. If you choose to hire a contractor from the network, payment terms are handled directly with that contractor.",
  },
  {
    category: "For Homeowners",
    question: "What kinds of projects can I submit?",
    answer:
      "BuildBright supports a wide range of home improvement projects including roofing, plumbing, electrical, HVAC, painting, flooring, remodeling, landscaping, and general repairs. If it involves your home, you can submit it.",
  },
  {
    category: "For Homeowners",
    question: "How fast will I hear from a contractor?",
    answer:
      "Most homeowners receive their first contractor response within 24 hours of submitting their project. In high-demand areas, responses often come within a few hours. You'll be notified by email and through the platform when a contractor expresses interest.",
  },
  {
    category: "For Homeowners",
    question: "Are the contractors on BuildBright reviewed?",
    answer:
      "Yes. All contractors on BuildBright go through a verification process that includes license checks, insurance confirmation, and profile review. We don't just list anyone — we focus on quality so you don't have to waste time filtering through unqualified bids.",
  },
  {
    category: "For Contractors",
    question: "How does BuildBright work for contractors?",
    answer:
      "BuildBright allows contractors to review relevant project opportunities based on trade, coverage area, availability, and BuildBright quality filters. Contractors choose whether to pursue available opportunities and manage communication directly with homeowners after details are unlocked.",
  },
  {
    category: "For Contractors",
    question: "How do I sign up as a contractor?",
    answer:
      "Head to BuildBright.app and click \"Join as a Contractor.\" You'll create your profile, add your license and insurance info, set your service area and trade categories, and set up claim credits for project opportunity access. Setup takes under 15 minutes."
  },
  {
    category: "For Contractors",
    question: "Do contractors compete for the same project opportunity?",
    answer:
      "BuildBright uses a controlled access system designed to keep project opportunity visibility focused and relevant. When a contractor unlocks a project opportunity, they are reviewing a request that passed BuildBright’s service-area and project-quality filters.",
  },
  {
    category: "Pricing & Credits",
    question: "How does the credit system work?",
    answer:
      "BuildBright uses a claim-credit system for contractors. Claim credits are used when a contractor chooses to unlock full details for a relevant project opportunity. Credit usage may vary by project size, type, and location. Credits never expire, so contractors can use them at their own pace.",
  },
  {
    category: "Pricing & Credits",
    question: "How do claim credits work?",
    answer:
      "Claim-credit requirements vary by project type and location. Contractors can view the required credit amount before unlocking a project opportunity. Additional credit packages may be available for contractors who want more flexibility in reviewing project opportunities.",
  },
  {
    category: "Pricing & Credits",
    question: "Can I buy more credits at any time?",
    answer:
      "Yes. You can purchase additional credits anytime from your contractor dashboard. We offer flexible packages so you can start small and scale up as you grow your pipeline through BuildBright.",
  },
  {
    category: "Project Request Review",
    question: "What makes BuildBright project requests different?",
    answer:
      "Every BuildBright project request starts with a homeowner who actively submitted details about planned home improvement work. We do not use scraped, aged, or recycled project requests. Requests are reviewed, organized, and shown to relevant contractors based on service type, coverage area, and quality filters.",
  },
  {
    category: "Project Request Review",
    question: "How does the claim system work?",
    answer:
      "When a new project opportunity is available in your area and trade category, you may receive a notification. You can review the basic project details — including scope, location, and budget if provided — and decide whether to unlock the full homeowner contact details using claim credits.",
  },
  {
    category: "Refund Policy",
    question: "What is the refund policy for contractors?",
    answer:
      "BuildBright reviews project requests before showing them to contractors. If an approved contractor unlocks a project opportunity and the contact information appears invalid, the contractor may submit a review request within 72 hours. Verified invalid contact information may be replaced or credited according to BuildBright policy."
  },
  {
    category: "Refund Policy",
    question: "What if I'm not satisfied with BuildBright overall?",
    answer:
      "We want every approved contractor to have a clear, useful experience. If you're experiencing issues with project opportunity volume, quality, or account performance, reach out to our support team directly. We can review your service area, project categories, and profile settings to help improve fit and visibility."
  },
];

const categories = [...new Set(faqs.map((f) => f.category))];

function AccordionItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-zinc-900 hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-inset"
        aria-expanded={isOpen}
      >
        <span className="font-semibold text-white text-sm sm:text-base pr-4">
          {question}
        </span>
        <span
          className={`text-orange-400 text-xl font-bold flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-45" : "rotate-0"
          }`}
        >
          +
        </span>
      </button>
      {isOpen && (
        <div className="px-5 py-4 bg-black border-t border-zinc-800">
          <p className="text-zinc-300 text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <main className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12 text-center">
          <p className="text-xs font-semibold tracking-[0.2em] text-orange-400 mb-3 uppercase">
            Support
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base max-w-xl mx-auto">
            Everything you need to know about BuildBright — whether you're a
            homeowner requesting project support or a contractor applying to join BuildBright.
          </p>
        </div>

        {/* FAQ sections by category */}
        {categories.map((cat) => (
          <div key={cat} className="mb-10">
            <h2 className="text-xs font-semibold tracking-widest text-orange-400 uppercase mb-4">
              {cat}
            </h2>
            <div className="space-y-3">
              {faqs
                .filter((f) => f.category === cat)
                .map((faq) => {
                  const globalIdx = faqs.indexOf(faq);
                  return (
                    <AccordionItem
                      key={globalIdx}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openIndex === globalIdx}
                      onToggle={() => toggle(globalIdx)}
                    />
                  );
                })}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="mt-12 rounded-2xl bg-zinc-900 border border-zinc-800 px-6 py-8 text-center">
          <h3 className="text-xl font-bold text-white mb-2">
            Still have questions?
          </h3>
          <p className="text-zinc-400 text-sm mb-5">
            Reach out to our team — we respond fast.
          </p>
          <a
            href="/#/contact"
            className="inline-block bg-orange-500 hover:bg-orange-400 text-white font-semibold px-6 py-3 rounded-lg text-sm transition-colors"
          >
            Contact Us
          </a>
        </div>
      </div>
    </main>
  );
}
